import { useMemo } from "react";
import { usePlayerInventoryQuery } from "../../../queries/usePlayerInventoryQuery";
import { usePlayerPositionQuery } from "../../../queries/usePlayerPositionQuery";
import { useAppStore } from "../../../store/appStore";
import type { Vec3 } from "../hooks/useBlueprintCommon";
import type { BlueprintItemWithActualObjectTypeId } from "./crossMatchBlueprint";

export function useClosestObjectTypesAt({
  missingBlueprintObjectTypesAt,
}: {
  missingBlueprintObjectTypesAt: BlueprintItemWithActualObjectTypeId[];
}) {
  const {
    terraformBuildable,
    terraformMineable,
    terraformSize,
    activeObjectTypeId,
  } = useAppStore();
  const { data: playerPosition } = usePlayerPositionQuery();
  const { data: playerInventory } = usePlayerInventoryQuery(activeObjectTypeId);

  return useMemo(() => {
    if (!playerPosition) {
      return [];
    }

    const isShowAllObjects = activeObjectTypeId === 0;

    const availableObjectTypes = new Map<number, number>();
    if (playerInventory) {
      for (const item of playerInventory) {
        const currentAmount = availableObjectTypes.get(item.objectTypeId) || 0;
        availableObjectTypes.set(
          item.objectTypeId,
          currentAmount + item.amount,
        );
      }
    }

    const totalActiveObjectTypeIdAmount = (playerInventory || []).reduce(
      (acc, item) => {
        if (item.objectTypeId === activeObjectTypeId) {
          return acc + item.amount;
        }
        return acc;
      },
      0,
    );

    const usedObjectTypes = new Map<number, number>();

    const closestObjectTypeAt = missingBlueprintObjectTypesAt
      .map((item) => {
        const playerTopPosition: Vec3 = [
          playerPosition[0],
          playerPosition[1] + 1,
          playerPosition[2],
        ];

        const dx = item.position[0] - playerTopPosition[0];
        const dy = item.position[1] - playerTopPosition[1];
        const dz = item.position[2] - playerTopPosition[2];
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        return {
          ...item,
          distance,
        };
      })
      .filter((item) => {
        if (item.distance > 10) {
          return false;
        }

        const isAirActualObjectTypeId = item.actualObjectTypeId === 1;
        if (isShowAllObjects) {
          if (terraformBuildable && isAirActualObjectTypeId) {
            const availableAmount =
              availableObjectTypes.get(item.objectTypeId) || 0;
            const usedAmount = usedObjectTypes.get(item.objectTypeId) || 0;
            return availableAmount > usedAmount;
          }

          if (terraformMineable && !isAirActualObjectTypeId) {
            return true;
          }
        }

        if (!isShowAllObjects) {
          if (
            activeObjectTypeId === item.objectTypeId &&
            isAirActualObjectTypeId
          ) {
            return true;
          }

          if (activeObjectTypeId === 1 && !isAirActualObjectTypeId) {
            return true;
          }
        }

        if (isShowAllObjects) {
          const availableAmount =
            availableObjectTypes.get(item.objectTypeId) || 0;
          const usedAmount = usedObjectTypes.get(item.objectTypeId) || 0;

          if (usedAmount < availableAmount) {
            usedObjectTypes.set(item.objectTypeId, usedAmount + 1);
            return true;
          }
          return false;
        }

        return false;
      })
      .sort((a, b) => a.distance - b.distance)
      .slice(
        0,
        isShowAllObjects || terraformMineable
          ? terraformSize
          : Math.min(terraformSize, totalActiveObjectTypeIdAmount ?? 0),
      );

    return closestObjectTypeAt;
  }, [
    playerPosition,
    activeObjectTypeId,
    playerInventory,
    missingBlueprintObjectTypesAt,
    terraformMineable,
    terraformSize,
    terraformBuildable,
  ]);
}
